/**
 * WCAG 2.1 relative luminance and contrast ratio, and the three thresholds the
 * requirements state (task 7.7; R18.7, R18.15, and the Platform colour rule of
 * R16.12).
 *
 * ## Why this is written out rather than imported
 *
 * design.md names this one of two deliberate non-dependencies: "WCAG contrast
 * ratio (R16.12, R18.7, R18.15) is a fixed twelve-line formula computed in-house
 * rather than pulled from a colour library; it is verified by property test
 * against the WCAG definition." The formula below is that twelve lines. It is also
 * the only form available here: `packages/domain` has no runtime dependencies at
 * all, so there is no library to reach for even if one were wanted.
 *
 * Both definitions are transcribed from the WCAG 2.1 glossary and are quoted in
 * full at their implementations, so the code can be read against the standard
 * without opening it:
 *
 * - relative luminance - https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * - contrast ratio - https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 *
 * ## Pure, like the rest of the package
 *
 * No DOM, no `getComputedStyle`, no colour parsing beyond the one stored format.
 * Colours arrive as text or as 8-bit triples and leave as numbers. A rendered
 * element's *actual* colours are a browser fact, which is why R18.6/R18.7
 * conformance is additionally scanned in Playwright (design.md, "Accessibility
 * testing"); this module supplies the arithmetic those scans and the master data
 * validation of R16.12 both need to agree on.
 */
/** The stored format of R16.12 and migration 0002, and the only one accepted. */
const HEX_COLOR_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/;
/** Highest value an 8-bit channel takes. */
const MAX_CHANNEL_VALUE = 255;
/**
 * Lowest contrast ratio the definition can produce: a colour against itself.
 *
 * Exported because it is the lower end of the range a generator draws from and
 * the value a "did the arithmetic run at all" assertion checks against.
 */
export const MIN_CONTRAST_RATIO = 1;
/** Highest contrast ratio the definition can produce: black against white. */
export const MAX_CONTRAST_RATIO = 21;
/**
 * Body text: at least 4.5 to 1 against its adjacent background (R18.7).
 *
 * WCAG 2.1 success criterion 1.4.3 at level AA, which R18.7 restates.
 */
export const BODY_TEXT_CONTRAST_MINIMUM = 4.5;
/**
 * Large text: at least 3 to 1 (R18.7).
 *
 * "Large" is R18.7's own definition - at least 18 point, or at least 14 point
 * bold - which {@link isLargeText} applies.
 */
export const LARGE_TEXT_CONTRAST_MINIMUM = 3;
/**
 * Non-text components: at least 3 to 1 against adjacent colours (R18.15).
 *
 * R18.15 names control boundaries, focus indicators, Initiative bars, milestone
 * markers and priority markers. Numerically equal to
 * {@link LARGE_TEXT_CONTRAST_MINIMUM} and kept separate anyway, because the two
 * come from different criteria and either could be raised without the other.
 */
export const NON_TEXT_CONTRAST_MINIMUM = 3;
/** Smallest point size R18.7 counts as large text at normal weight. */
export const LARGE_TEXT_MIN_POINT_SIZE = 18;
/** Smallest point size R18.7 counts as large text at bold weight. */
export const LARGE_TEXT_MIN_BOLD_POINT_SIZE = 14;
/** Whether `value` is a well-formed lowercase `#rrggbb` colour. */
export function isHexColor(value) {
    return HEX_COLOR_PATTERN.test(value);
}
/**
 * Narrows a string to {@link HexColor}, throwing on anything that is not a
 * lowercase `#rrggbb` colour.
 *
 * Throwing is right here for the same reason `plainDate` throws: the alternative
 * is a colour that silently contributes the wrong luminance, which shows up as a
 * Platform colour that passed validation and is unreadable on screen. A *submitted*
 * colour is a different matter - the user has to be told which field is wrong
 * rather than shown a stack trace - and `validatePlatformColor` reports that as a
 * finding instead of calling this.
 */
export function hexColor(value) {
    if (!isHexColor(value)) {
        throw new RangeError(`Not a lowercase #rrggbb colour: ${JSON.stringify(value)}`);
    }
    return value;
}
/**
 * The notations {@link canonicalizeColor} understands on the way in.
 *
 * `#RRGGBB` in any case, with or without the `#`; the three-digit shorthand
 * `#rgb`, which CSS expands by doubling each digit; and `rgb(r, g, b)` with
 * integer channels. Surrounding whitespace is ignored.
 */
const INPUT_NOTATIONS = Object.freeze({
    sixDigit: /^#?([0-9a-f]{6})$/i,
    threeDigit: /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i,
    functional: /^rgba?\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*(?:[,/]\s*[\d.]+\s*)?\)$/i,
});
/**
 * Reduces a colour a human typed or pasted to the one stored spelling, or
 * `undefined` when it names no colour at all.
 *
 * ## Why this exists next to a validator that refuses to normalise
 *
 * {@link isHexColor} accepts exactly lowercase `#rrggbb`, and that strictness is a
 * correctness rule rather than a style preference: R16.12's uniqueness is plain
 * string equality, both in `platform_color_active_unique` and in
 * `validatePlatformColor`, so two spellings of one colour would let two active
 * Platforms hold it. That argument is about what gets *stored and compared*. It is
 * not an argument for making an administrator retype `#FB7702` in lowercase.
 *
 * So the invariant is kept and the burden moved: this function canonicalises at
 * the boundary where input arrives, explicitly and visibly, and everything behind
 * it still sees exactly one spelling. The narrowing is deliberate and at the
 * caller's request - which is what the type's own note asks for - rather than
 * hidden inside the validator.
 *
 * Returning `undefined` rather than throwing keeps a genuinely meaningless entry
 * ("banana", an empty field) on the finding path, where the administrator is told
 * which field is wrong, instead of turning it into a thrown fault.
 */
export function canonicalizeColor(input) {
    const trimmed = input.trim();
    const sixDigit = INPUT_NOTATIONS.sixDigit.exec(trimmed);
    if (sixDigit?.[1] !== undefined) {
        return hexColor(`#${sixDigit[1].toLowerCase()}`);
    }
    const threeDigit = INPUT_NOTATIONS.threeDigit.exec(trimmed);
    if (threeDigit !== null) {
        // CSS expands `#abc` by doubling each digit, so `#abc` is `#aabbcc`.
        const [, red, green, blue] = threeDigit;
        const doubled = [red, green, blue].map((digit) => `${digit ?? ''}${digit ?? ''}`).join('');
        return hexColor(`#${doubled.toLowerCase()}`);
    }
    const functional = INPUT_NOTATIONS.functional.exec(trimmed);
    if (functional !== null) {
        const channels = [functional[1], functional[2], functional[3]].map((raw) => Number.parseInt(raw ?? '', 10));
        if (channels.some((channel) => !Number.isInteger(channel) || channel > MAX_CHANNEL_VALUE)) {
            return undefined;
        }
        const [red, green, blue] = channels;
        return formatHexColor(srgbColor(red, green, blue));
    }
    return undefined;
}
/**
 * {@link canonicalizeColor}, falling back to the input unchanged.
 *
 * For a write path that wants to canonicalise what it can while leaving anything
 * uninterpretable for the validation rules to report as a finding, rather than
 * silently substituting a colour nobody asked for.
 */
export function canonicalizeColorOrKeep(input) {
    return canonicalizeColor(input) ?? input;
}
/** Builds an {@link SrgbColor} from channel values, rejecting anything but integers 0-255. */
export function srgbColor(red, green, blue) {
    for (const [name, value] of [
        ['red', red],
        ['green', green],
        ['blue', blue],
    ]) {
        if (!Number.isInteger(value) || value < 0 || value > MAX_CHANNEL_VALUE) {
            throw new RangeError(`sRGB ${name} channel must be an integer from 0 to ${String(MAX_CHANNEL_VALUE)}, received ${String(value)}`);
        }
    }
    return { red, green, blue };
}
/** The channels of a {@link HexColor}. Cannot fail: the brand guarantees the shape. */
export function toSrgbColor(color) {
    const match = HEX_COLOR_PATTERN.exec(color);
    if (match === null) {
        // Unreachable for a branded value; `hexColor` is the only way to obtain one.
        throw new RangeError(`Not a lowercase #rrggbb colour: ${JSON.stringify(color)}`);
    }
    // The capture groups are non-optional in the pattern, so the fallbacks satisfy
    // `noUncheckedIndexedAccess` and are never taken.
    return {
        red: Number.parseInt(match[1] ?? '', 16),
        green: Number.parseInt(match[2] ?? '', 16),
        blue: Number.parseInt(match[3] ?? '', 16),
    };
}
/** An {@link SrgbColor} in the stored lowercase `#rrggbb` form. */
export function formatHexColor(color) {
    const { red, green, blue } = srgbColor(color.red, color.green, color.blue);
    const channel = (value) => value.toString(16).padStart(2, '0');
    return hexColor(`#${channel(red)}${channel(green)}${channel(blue)}`);
}
/**
 * Parses a colour and returns its channels, or `undefined` when the text is not a
 * lowercase `#rrggbb` colour.
 *
 * The non-throwing entry point, for the one caller that has to report a malformed
 * value as a finding rather than fail on it.
 */
export function parseHexColor(value) {
    return isHexColor(value) ? toSrgbColor(value) : undefined;
}
/**
 * One channel, gamma-expanded to its linear-light value.
 *
 * WCAG 2.1, relative luminance: for each of R, G and B expressed as `c8bit / 255`,
 *
 * ```text
 * if c <= 0.03928 then c / 12.92 else ((c + 0.055) / 1.055) ^ 2.4
 * ```
 *
 * The `0.03928` is WCAG 2.1's own constant. The sRGB standard and WCAG 2.2 write
 * `0.04045`, and the difference is unobservable for 8-bit input: the largest
 * channel below either threshold is 10/255 = 0.039215..., which takes the linear
 * branch under both, and the next value up, 11/255 = 0.043137..., takes the power
 * branch under both. So the two spellings agree on every colour this package can
 * be handed, and the WCAG 2.1 constant is used because WCAG 2.1 AA is the level
 * R18.6, R18.7 and R18.15 are stated at.
 */
function linearizeChannel(value) {
    const channel = value / MAX_CHANNEL_VALUE;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}
/**
 * Relative luminance: 0 for black, 1 for white.
 *
 * WCAG 2.1: `L = 0.2126 * R + 0.7152 * G + 0.0722 * B`, where each channel has
 * been linearised as described on {@link linearizeChannel}. The coefficients are
 * the ITU-R BT.709 luma weights the standard cites, not a tunable.
 */
export function relativeLuminance(color) {
    const { red, green, blue } = srgbColor(color.red, color.green, color.blue);
    return (0.2126 * linearizeChannel(red) +
        0.7152 * linearizeChannel(green) +
        0.0722 * linearizeChannel(blue));
}
/**
 * Contrast ratio between two colours, from 1 (a colour against itself) to 21
 * (black against white).
 *
 * WCAG 2.1: `(L1 + 0.05) / (L2 + 0.05)`, with `L1` the lighter and `L2` the darker
 * of the two relative luminances. Symmetric by construction - the lighter of the
 * pair is picked here rather than assumed from the argument order - which is why
 * no call site has to know whether it is passing foreground first.
 */
export function contrastRatio(a, b) {
    const first = relativeLuminance(a);
    const second = relativeLuminance(b);
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
/** {@link contrastRatio} for two colours in the stored `#rrggbb` form. */
export function hexContrastRatio(a, b) {
    return contrastRatio(toSrgbColor(a), toSrgbColor(b));
}
/**
 * Whichever of two candidate foreground colours contrasts more strongly with
 * `background`.
 *
 * Exists because a fixed foreground cannot serve an arbitrary background, and
 * Platform colours became arbitrary: `platform-color.ts` records that R16.12's
 * 4.5:1 minimum was removed by product decision, so an administrator may store a
 * pale colour on which white text fails and a near-black one on which dark text
 * fails. Picking per background is the only way an Initiative bar's title keeps
 * the best contrast available to it (R18.7) without constraining the palette.
 *
 * Ties resolve to `first`, so the caller's preferred default wins when neither
 * candidate is better - which keeps the result a pure function of its inputs
 * rather than of argument order at the call site.
 */
export function higherContrastColor(background, first, second) {
    return contrastRatio(first, background) >= contrastRatio(second, background) ? first : second;
}
/** {@link higherContrastColor} for colours in the stored `#rrggbb` form. */
export function higherContrastHexColor(background, first, second) {
    return hexContrastRatio(first, background) >= hexContrastRatio(second, background)
        ? first
        : second;
}
/**
 * Whether a ratio reaches a threshold.
 *
 * `>=`, and nothing is rounded first. The criteria say "at least", so a pair whose
 * computed ratio falls a hair under 4.5 fails - which is the conservative reading
 * and the one the automated scans of design.md apply. Rounding to two decimals
 * first, as some authoring tools do, would accept pairs at 4.495 and make the
 * verdict depend on a display convention.
 */
export function meetsContrastMinimum(ratio, minimum) {
    return ratio >= minimum;
}
/**
 * Whether text at `pointSize` and `bold` counts as large text under R18.7: at
 * least 18 point, or at least 14 point bold.
 */
export function isLargeText(pointSize, bold) {
    return bold ? pointSize >= LARGE_TEXT_MIN_BOLD_POINT_SIZE : pointSize >= LARGE_TEXT_MIN_POINT_SIZE;
}
/** The scale R18.7 places text of this size and weight in. */
export function textScale(pointSize, bold) {
    return isLargeText(pointSize, bold) ? 'large' : 'body';
}
/** The threshold R18.7 applies to a text scale. */
export function textContrastMinimum(scale) {
    return scale === 'large' ? LARGE_TEXT_CONTRAST_MINIMUM : BODY_TEXT_CONTRAST_MINIMUM;
}
/** Whether a foreground and background pair is readable as body text (R18.7). */
export function meetsBodyTextContrast(foreground, background) {
    return meetsContrastMinimum(contrastRatio(foreground, background), BODY_TEXT_CONTRAST_MINIMUM);
}
/** Whether a foreground and background pair is readable as large text (R18.7). */
export function meetsLargeTextContrast(foreground, background) {
    return meetsContrastMinimum(contrastRatio(foreground, background), LARGE_TEXT_CONTRAST_MINIMUM);
}
/**
 * Whether text of this size and weight is readable against a background (R18.7).
 *
 * The two-threshold rule in one call, so no caller has to remember which size
 * boundary goes with which ratio.
 */
export function meetsTextContrast(foreground, background, pointSize, bold) {
    return meetsContrastMinimum(contrastRatio(foreground, background), textContrastMinimum(textScale(pointSize, bold)));
}
/** Whether a non-text component reaches the 3 to 1 of R18.15 against an adjacent colour. */
export function meetsNonTextContrast(component, adjacent) {
    return meetsContrastMinimum(contrastRatio(component, adjacent), NON_TEXT_CONTRAST_MINIMUM);
}
//# sourceMappingURL=contrast.js.map